/* eslint-disable react/jsx-one-expression-per-line */
/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 ~ Copyright 2020 Adobe Systems Incorporated
 ~
 ~ Licensed under the Apache License, Version 2.0 (the "License");
 ~ you may not use this file except in compliance with the License.
 ~ You may obtain a copy of the License at
 ~
 ~     http://www.apache.org/licenses/LICENSE-2.0
 ~
 ~ Unless required by applicable law or agreed to in writing, software
 ~ distributed under the License is distributed on an "AS IS" BASIS,
 ~ WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 ~ See the License for the specific language governing permissions and
 ~ limitations under the License.
 ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

import React from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import { EditableComponentPropsType } from '../../../utils/prop-types'
import withComponentRefresh from '../../../utils/aem/editor/withComponentRefresh'
import { useExtractEditableProps } from '../../../utils/aem/editor/extractEditableProps'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../functional/ErrorBoundary'
import { ConsentRequirement } from '../../../types/visibility-gate'

interface UserSegmentVisibility {
  segmentName: string
}

interface NavigationItemConfigModelProps {
  navLink?: NavLink
  banner?: {
    imagePath: string
    imgAlt: string
    title: string
    subtitle: string
    linkUrl: string
    ariaLabel: string
  }
  bannerTracking?: {
    enableCampaignTracking: boolean
    [key: string]: string | boolean
  }
  visibleTo?: string
  consents?: ConsentRequirement[]
  userSegmentVisibility?: UserSegmentVisibility[]
  userSegmentVisibilityNames?: string[]
}

interface NavLink {
  firstLabel: string
  secondLabel: string
  imagePath: string
  imageAlt: string
  linkUrl: string
  ariaLabel: string
  additionalClasses: string
  showInMiscNavigation: boolean
  slideSubmenu?: NavLink
}

interface UserSegmentVisibilityRendererProps {
  userSegmentVisibilityNames?: string[]
  userSegmentVisibility?: UserSegmentVisibility[]
}

const UserSegmentVisibilityRenderer: React.FC<
  UserSegmentVisibilityRendererProps
> = ({ userSegmentVisibilityNames, userSegmentVisibility }) => {
  if (userSegmentVisibilityNames && userSegmentVisibilityNames.length > 0) {
    return (
      <div>
        <h3>User Segment Visibility (Edit Mode)</h3>
        <div>
          {userSegmentVisibilityNames.map((name) => (
            <div key={name}>{name}</div>
          ))}
        </div>
      </div>
    )
  }

  if (userSegmentVisibility && userSegmentVisibility.length > 0) {
    return (
      <div>
        <h3>User Segment Visibility</h3>
        <div>
          {userSegmentVisibility.map((segment) => (
            <div key={segment.segmentName}>{segment.segmentName}</div>
          ))}
        </div>
      </div>
    )
  }

  return null
}

const NavigationItemConfig: React.FC<
  EditableComponentPropsType<NavigationItemConfigModelProps>
> = withComponentRefresh((props) => {
  const {
    navLink,
    banner,
    bannerTracking,
    visibleTo,
    userSegmentVisibility,
    userSegmentVisibilityNames,
    consents,
    ...rest
  } = props.model
  const editableProps = useExtractEditableProps(props, rest)

  return (
    <EditableComponent {...editableProps}>
      <div>
        <div>
          <h1>Navigation Item Configuration</h1>
        </div>
        <div>
          <b>First Label:</b> {navLink?.firstLabel}
        </div>
        <div>
          <b>Second Label:</b> {navLink?.secondLabel}
        </div>
        <div>
          <b>Image Path:</b> {navLink?.imagePath}
        </div>
        <div>
          <b>Image Alt:</b> {navLink?.imageAlt}
        </div>
        <div>
          <b>Link URL:</b> {navLink?.linkUrl}
        </div>
        <div>
          <b>Aria Label:</b> {navLink?.ariaLabel}
        </div>
        <div>
          <b>Additional Classes:</b> {navLink?.additionalClasses}
        </div>
        <div>
          <b>Slide Submenu:</b> {navLink?.slideSubmenu?.toString()}
        </div>
        <div>
          <b>Show in Misc Navigation:</b>{' '}
          {navLink?.showInMiscNavigation?.toString()}
        </div>
        <div>
          <h3>Banner</h3>
        </div>
        {banner ? (
          <div>
            <div>
              <b>Banner Image Path:</b> {banner?.imagePath}
            </div>
            <div>
              <b>Banner Image Alt:</b> {banner?.imgAlt}
            </div>
            <div>
              <b>Banner Title:</b> {banner?.title}
            </div>
            <div>
              <b>Banner Subtitle:</b> {banner?.subtitle}
            </div>
            <div>
              <b>Banner Link URL:</b> {banner?.linkUrl}
            </div>
            <div>
              <b>Banner Link Aria Label:</b> {banner?.ariaLabel}
            </div>
          </div>
        ) : (
          'Banner not configured'
        )}
        <div>
          <h3>Banner Tracking</h3>
        </div>
        {bannerTracking ? (
          <div>
            <div>
              <b>Enable Banner Campaign Tracking: </b>
              {bannerTracking?.enableCampaignTracking?.toString()}
            </div>
            <div>
              <b>data-pmi-class: </b>
              {bannerTracking?.['data-pmi-class']}
            </div>
            <div>
              <b>data-pmi-promotion-id: </b>
              {bannerTracking?.['data-pmi-promotion-id']}
            </div>
            <div>
              <b>data-pmi-promotion-name: </b>
              {bannerTracking?.['data-pmi-promotion-name']}
            </div>
            <div>
              <b>data-pmi-promotion-creative: </b>
              {bannerTracking?.['data-pmi-promotion-creative']}
            </div>
            <div>
              <b>data-pmi-promotion-position: </b>
              {bannerTracking?.['data-pmi-promotion-position']}
            </div>
          </div>
        ) : (
          'Banner tracking not configured'
        )}
        <div>
          <h3>Visible To</h3>
        </div>
        <div>
          {visibleTo ? visibleTo?.toString() : 'Visibility not configured'}
        </div>
        <br />
        <br />
        <br />
        <div>Consents</div>
        <div>
          {consents && consents.length > 0
            ? consents.map((consent) => (
                <div key={consent.id}>
                  {consent.id} {consent.enabled ? '✅' : '❌'}
                </div>
              ))
            : 'No consents configured'}
        </div>
        <br />
        <br />
        <br />
        <UserSegmentVisibilityRenderer
          userSegmentVisibilityNames={userSegmentVisibilityNames}
          userSegmentVisibility={userSegmentVisibility}
        />
      </div>
    </EditableComponent>
  )
})

const NavigationItemConfigWithErrorBoundary = withErrorBoundary(
  NavigationItemConfig,
  {
    componentName: 'NavigationItemConfig',
    fallback: <ErrorFallback componentName="NavigationItemConfig" />
  }
)

export default NavigationItemConfigWithErrorBoundary
